import { Check, Copy } from "lucide-react";
import { useSelector } from "react-redux";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import {
  ActionBarPrimitive,
  AgentToolPart,
  MessagePrimitive,
  showsCard,
  useAuiState,
  type ToolArtifact,
  type ToolCallMessagePartProps,
} from "../aui";
import type { AgentState } from "../store/agentSlice";
import { MarkdownText } from "./MarkdownText";
import { closeOpenMarkdown } from "./openMarkdown";
import { ToolSteps } from "./ToolSteps";
import { useThreadId } from "./useThreadId";

type Part = { type: string; artifact?: unknown; result?: unknown };

/** A tool call that shows a card (a native card, an approval, a pick) rather than a line. */
function isCard(part: Part): boolean {
  return part.type === "tool-call" && showsCard(part.artifact, part.result);
}

/** A step still running: a tool line, not a card (a card's part never has a result of its own). */
function stillRuns(part: Part): boolean {
  const state = (part.artifact as ToolArtifact | undefined)?.state;
  return part.type === "tool-call" && !isCard(part) && (state === "running" || state === "retry");
}

// Module level, so the grouping keeps its identity across renders.
const groupTools = (part: Part) =>
  part.type === "tool-call" && !isCard(part) ? (["group-tools"] as const) : null;

/** Before the answer's first words, as the lip says it. */
function Thinking() {
  return (
    <span
      role="status"
      className="w-fit bg-gradient-to-r from-muted-foreground via-foreground to-muted-foreground bg-[length:200%_100%] bg-clip-text text-xs text-transparent motion-safe:animate-shimmer"
    >
      Thinking…
    </span>
  );
}

/** The model that wrote the answer, Stopped on one a stop cut short, and Copy. */
function MessageMeta({ stopped }: { stopped: boolean }) {
  const model = useAuiState((state) => state.message.metadata.custom.model) as string | null;
  const copied = useAuiState((state) => state.message.isCopied);
  const label = [model, stopped && "Stopped"].filter(Boolean).join(" · ");
  return (
    <ActionBarPrimitive.Root className="flex items-center gap-1 text-xs text-muted-foreground">
      {label && <span>{label}</span>}
      <ActionBarPrimitive.Copy asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-6 w-6 max-sm:h-9 max-sm:w-9"
          aria-label="Copy"
          data-compact=""
        >
          {copied ? <Check className="!size-3.5" /> : <Copy className="!size-3.5" />}
        </Button>
      </ActionBarPrimitive.Copy>
    </ActionBarPrimitive.Root>
  );
}

/**
 * An answer. One turn can be several stored steps (tool calls, then text), each its own message:
 * they read as one, so the spacing between them is tight and the meta row ends the turn only.
 */
export function AssistantMessage() {
  const threadId = useThreadId();
  const running = useAuiState((state) => state.message.status?.type === "running");
  // The last answer of a stopped run ends mid-sentence, like a streaming one.
  const stopped = useSelector(
    (state: { agent: AgentState }) =>
      threadId != null && state.agent.threads[threadId]?.run?.status === "cancelled"
  );
  // The answer a stopped run left: the last assistant message, past any user messages queued or
  // being sent after it. A stopped run that wrote nothing leaves the earlier answer alone.
  const stoppedHere = useAuiState((state) => {
    const messages = state.thread.messages;
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      const message = messages[index];
      const custom = message.metadata.custom as { pending?: boolean };
      if (message.role === "user" && (custom.pending || message.id.startsWith("out:"))) continue;
      return message.role === "assistant" && message.id === state.message.id;
    }
    return false;
  });
  // The turn still runs: the answer is running and no message of the user's follows this one.
  const turnRunning = useAuiState(
    (state) =>
      state.thread.isRunning &&
      state.thread.messages.slice(state.message.index + 1).every((m) => m.role !== "user")
  );
  const continues = useAuiState(
    (state) => state.thread.messages[state.message.index + 1]?.role === "assistant"
  );
  const continued = useAuiState(
    (state) => state.thread.messages[state.message.index - 1]?.role === "assistant"
  );
  // A card in this step or a later one of the turn shows the result: the lines fold.
  // assistant-ui adds a placeholder answer while a run starts after a user message; a message
  // queued behind an answer that is still streaming must not get a second one.
  const placeholderTwice = useAuiState(
    (state) =>
      (state.message.metadata as { isOptimistic?: boolean }).isOptimistic === true &&
      state.thread.messages.some(
        (m) => m.id !== state.message.id && m.role === "assistant" && m.status?.type === "running"
      )
  );
  // A step of this turn is blocked on a choice: its message waits on one (its own, or one a code
  // cell asked for) while a call in it still runs. Its card, and the call's spinner, already say
  // the turn is live, so there is no Thinking under it. Once the call has ended, a choice still
  // open no longer holds the turn up, and Thinking shows again.
  const blockedOnChoice = useAuiState((state) => {
    const messages = state.thread.messages;
    for (let index = state.message.index; messages[index]?.role === "assistant"; index -= 1) {
      const message = messages[index];
      if (message.status?.type === "requires-action" && message.parts.some(stillRuns)) return true;
    }
    return false;
  });
  const cardFollows = useAuiState((state) => {
    const messages = state.thread.messages;
    for (let index = state.message.index; messages[index]?.role === "assistant"; index += 1) {
      if (messages[index].parts.some(isCard)) return true;
    }
    return false;
  });
  if (placeholderTwice) return null;
  return (
    <MessagePrimitive.Root
      role="article"
      aria-label="Steddy Assistant"
      className={cn("flex flex-col gap-2", continued && "-mt-3")}
    >
      <MessagePrimitive.GroupedParts groupBy={groupTools}>
        {({ part, children }) => {
          switch (part.type) {
            case "group-tools":
              return (
                <ToolSteps
                  count={part.indices.length}
                  running={turnRunning}
                  cardFollows={cardFollows}
                >
                  {children}
                </ToolSteps>
              );
            case "text": {
              const streaming = part.status?.type === "running";
              const unfinished = streaming || (stopped && stoppedHere);
              return (
                <MarkdownText
                  text={unfinished ? closeOpenMarkdown(part.text) : part.text}
                  streaming={streaming}
                />
              );
            }
            case "tool-call":
              return <AgentToolPart {...(part as unknown as ToolCallMessagePartProps)} />;
            case "indicator":
              return blockedOnChoice ? <></> : <Thinking />;
            default:
              return <></>; // reasoning and anything else stay out of the answer
          }
        }}
      </MessagePrimitive.GroupedParts>
      {!running && !continues && <MessageMeta stopped={stopped && stoppedHere} />}
    </MessagePrimitive.Root>
  );
}
